"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import {
	add_product_to_favorite_mutation,
	add_product_to_notification_mutation,
	add_to_cart_mutation,
	get_favorite_products_query,
	remove_all_from_cart_mutation,
	remove_from_cart_mutation,
} from "@core/services/products";
import { is_function } from "@core/helpers/function";
import { toast_configs } from "@core/constants/toast_configs";
import { useSearchParams } from "next/navigation";
import { get_cookie, set_cookie } from "@core/helpers/cookie";
import { parse_local_cart } from "@core/helpers/local-cart";
import { get_cart_query } from "@core/services/auth";
import useTranslationOr from "./useTranslationOr";
import { array_unique_by_key } from "@core/helpers/array";
import countUnits from "@core/helpers/count-units";
import { useLanguageContext } from "../contexts/LanguagesContext";
import {
	check_product_availability,
	check_product_offer,
	get_default_mix_variant,
	get_initial_selected_labels,
	get_preselected_selection,
	get_mix_variant_limit_min,
	get_mix_variant_limit_max,
	get_offer_remaining_seconds,
} from "@core/helpers/product";

// Single source of truth: @core/helpers/count-units
export const count_units = countUnits;
// `initial_labels` ("key,key", the `?selected-labels=` value) replaces reading the URL here, so a caller that already knows it
// (e.g. from the server `searchParams`) does not subscribe to the router. `useProduct` below keeps the old behaviour.
export const useProductBase = (
	data = {},
	exception_variant_key = null,
	set_query_param = false,
	default_favorites_limit = 15,
	{ initial_labels, preselected_label_ids } = {},
) => {
	const { is_auth, cart, set_cart } = useAuthContext();
	const t_or = useTranslationOr();
	// ciseco / chawk-bazar: cart item names must follow the active site language; "fa" when no language is known (same as before).
	const lang = useLanguageContext()?.language || "fa";

	const create_query_string = useCallback(
		(name, value) => {
			const params = new URLSearchParams(window.location.search);
			params.set(name, value);
			const decodedQuery = decodeURIComponent(params.toString());

			window.history.replaceState(
				{},
				"",
				window.location.protocol +
				"//" +
				window.location.host +
				window.location.pathname +
				"?" +
				decodedQuery,
			);
		},
		[],
	);

	const [add_to_cart_loading, set_add_to_cart_loading] = useState(() => {
		let obj = {};
		data?.mix_variant?.forEach((mx) => (obj[mx?.keys] = false));
		return obj;
	});
	const [remove_from_cart_loading, set_remove_from_cart_loading] = useState(
		() => {
			let obj = {};
			data?.mix_variant?.forEach((mx) => (obj[mx?.keys] = false));
			return obj;
		},
	);
	const [add_to_favorite_loading, set_add_to_favorite_loading] = useState(
		() => {
			let obj = {};
			obj[data?.id] = false;
			return obj;
		},
	);
	const [get_favorites_loading, set_get_favorites_loading] = useState(true);

	const [favorites, set_favorites] = useState([]);
	const [favorites_paginatopn, set_favorites_paginatopn] = useState({ page: 1, limit: default_favorites_limit });

	const [is_notify, set_is_notify] = useState(() => {
		let obj = {};
		if (data?.mix_variant?.length > 0) {
			data?.mix_variant?.forEach((mx) => (obj[mx?.keys] = mx?.is_notify));
		} else {
			obj[data?.id] = data?.is_notify;
		}
		return obj;
	});
	const [is_favorite, set_is_favorite] = useState(() => {
		let obj = {};

		obj[data?.id] = data?.is_favorite;

		return obj;
	});
	const [selected_mix_variant, set_selected_mix_variant] = useState(() =>
		get_default_mix_variant(data),
	);
	const [selected_labels, set_selected_labels] = useState(() =>
		get_initial_selected_labels({ data, labels_query: initial_labels, exception_variant_key, preselected_label_ids }),
	);

	// console.log('selected_labels',selected_labels,data)

	const exception_variant = useMemo(() => {
		const variant = data?.variants?.find(
			(variant) => variant?.variant?.key === exception_variant_key,
		);
		const variant_id = variant?.variant?.id;

		let _selected_labels = { ...(selected_labels || {}) };
		delete _selected_labels?.[variant_id];
		let mix_variants = [];
		let is_all_mix_variants_stop_production = false;
		let is_all_mix_variants_exist = true;
		if (variant_id) {
			mix_variants = data?.mix_variant?.filter((mx) => {
				const keys = mx?.keys?.filter(
					(key) => key?.split("-")[0] !== variant_id,
				);
				return keys?.toString() === Object.values(_selected_labels)?.toString();
			});
			is_all_mix_variants_exist = !!mix_variants?.find((mx) =>
				check_product_availability(mx),
			);
			if (!is_all_mix_variants_exist) {
				is_all_mix_variants_exist = !!mix_variants?.find((mx) =>
					mx?.sub_prices?.find((sub) => check_product_availability(sub)),
				);
			}
		} else {
			mix_variants = [selected_mix_variant];
			is_all_mix_variants_exist =
				check_product_availability(selected_mix_variant);
			if (!is_all_mix_variants_exist) {
				is_all_mix_variants_exist = selected_mix_variant?.sub_prices?.find(
					(sub) => check_product_availability(sub),
				);
			}
		}
		if (!is_all_mix_variants_exist) {
			is_all_mix_variants_stop_production = !!mix_variants?.find(
				(mx) => mx?.details?.count_status === 5,
			);
		}
		return {
			variant,
			id: variant_id,
			_selected_labels,
			mix_variants,
			is_all_mix_variants_exist,
			is_all_mix_variants_stop_production,
		};
	}, [
		selected_labels,
		exception_variant_key,
		data?.variants,
		selected_mix_variant,
		data?.mix_variant,
	]);

	const available_variants = useMemo(() => {
		const items = data?.mix_variant?.filter((item) =>
			check_product_availability(item),
		);
		const _variants = [];
		const variant_labels = {};
		items?.forEach((item) => {
			Object?.keys(item?.labels_obj || {})?.forEach((variant_key) => {
				const variant = data?.variants?.find(
					(variant) => variant?.variant?.key === variant_key,
				);
				if (variant) {
					variant_labels[variant?.variant?.key] = {
						variant: variant?.variant,
						labels: [...(variant_labels[variant?.variant?.key]?.labels || [])],
					};
					const labels = variant?.labels?.filter(
						(label) => label?.id === item?.labels_obj[variant_key]?._id,
					);
					if (labels?.length > 0) {
						variant_labels[variant?.variant?.key]?.labels?.push(...labels);
					}
				}
			});
		});
		Object.keys(variant_labels || {})?.forEach((key) => {
			_variants.push({
				variant: variant_labels?.[key]?.variant,
				labels: array_unique_by_key(variant_labels?.[key]?.labels || [], "id"),
			});
		});
		return _variants;
	}, [data]);

	// const variants_with_disabled_unavailable = useMemo(() => {
	//   const items = data?.mix_variant;
	//   const _variants = [];
	//   const variant_labels = {};
	//   items?.forEach((item) => {
	//     const available = check_product_availability(item);
	//     Object?.keys(item?.labels_obj || {})?.forEach((variant_key) => {
	//       const variant = data?.variants?.find(
	//         (variant) => variant?.variant?.key === variant_key,
	//       );
	//       if (variant) {
	//         variant_labels[variant?.variant?.key] = {
	//           variant: variant?.variant,
	//           labels: [...(variant_labels[variant?.variant?.key]?.labels || [])],
	//         };
	//         let labels = variant?.labels?.filter(
	//           (label) => label?.id === item?.labels_obj[variant_key]?._id,
	//         );
	//         if (labels?.length > 0) {
	//           if (!available) {
	//             labels = labels?.map(label => ({ ...label, disabled: true }))
	//           }
	//           variant_labels[variant?.variant?.key]?.labels?.push(...labels);
	//         }
	//       }
	//     });
	//   });
	//   Object.keys(variant_labels || {})?.forEach((key) => {
	//     _variants.push({
	//       variant: variant_labels?.[key]?.variant,
	//       labels: array_unique_by_key(variant_labels?.[key]?.labels || [], "id"),
	//     });
	//   });
	//   return _variants;
	// }, [data])

	const variants_with_disabled_unavailable = useMemo(() => {
		const items = data?.mix_variant;
		const variant_labels = {};

		items?.forEach((item) => {
			const available = check_product_availability(item);

			Object?.keys(item?.labels_obj || {})?.forEach((variant_key) => {
				const variant = data?.variants?.find(
					(variant) => variant?.variant?.key === variant_key,
				);

				if (!variant) return;

				if (!variant_labels[variant_key]) {
					variant_labels[variant_key] = {
						variant: variant.variant,
						labels: {}
					};
				}

				const label_id = item?.labels_obj[variant_key]?._id;

				const label = variant?.labels?.find(l => l.id === label_id);
				if (!label) return;

				if (!variant_labels[variant_key].labels[label_id]) {
					variant_labels[variant_key].labels[label_id] = {
						...label,
						hasAvailable: false
					};
				}

				if (available) {
					variant_labels[variant_key].labels[label_id].hasAvailable = true;
				}
			});
		});

		return Object.keys(variant_labels).map((key) => ({
			variant: variant_labels[key].variant,
			labels: Object.values(variant_labels[key].labels).map(label => ({
				...label,
				disabled: !label.hasAvailable
			}))
		}));
	}, [data]);


	const item_count_in_cart = useCallback(
		({ mix_variant = undefined, bundle_id = undefined } = {}) => {
			let item;
			if (mix_variant) {
				item = mix_variant;
			} else {
				item = selected_mix_variant;
			}
			const items = cart?.products?.filter(
				(_item) => _item?.product_id === data?.id,
			);

			// the given bundle (core 14); it was compared with `data.id`, so a bundle shown inside a product page or card never
			// showed its count (memory-market, isource)
			if (bundle_id) {
				return cart?.products?.find((_item) => _item?.bundle_id === bundle_id)
					?.count;
			}

			if (item?.price_key) {
				return items?.find(
					(_item) =>
						_item?.mix_variant_keys?.toString() === item?.keys?.toString() &&
						_item?.price_key === item?.price_key,
				)?.count;
			} else {
				return items?.find(
					(_item) =>
						_item?.mix_variant_keys?.toString() === item?.keys?.toString() &&
						!_item?.price_key,
				)?.count;
			}
		},
		[cart, selected_mix_variant],
	);

	const mix_variant_limit_min = useCallback(
		({ mix_variant = undefined, bundle = undefined } = {}) => {
			let item;
			if (mix_variant) {
				item = mix_variant;
			} else {
				item = selected_mix_variant;
			}
			return get_mix_variant_limit_min(item, bundle);
		},
		[selected_mix_variant?.details],
	);

	const mix_variant_limit_max = useCallback(
		({ mix_variant = undefined, bundle = undefined } = {}) => {
			let item;
			if (mix_variant) {
				item = mix_variant;
			} else {
				item = selected_mix_variant;
			}
			return get_mix_variant_limit_max(item, bundle);
		},
		[selected_mix_variant?.details],
	);

	const select_variant_handler = useCallback(
		(label_id, variant_id) => {
			set_selected_labels((prev) => {
				let new_keys = {
					...(prev || {}),
					[variant_id]: `${variant_id}-${label_id}`,
				};

				return new_keys;
			});
		},
		[data],
	);

	const selected_label_keys = useMemo(() => {
		let label_keys = "";
		Object.values(
			exception_variant?.variant
				? exception_variant?._selected_labels
				: selected_labels || {},
		)?.forEach((label) => {
			const label_id = label?.split("-")[1];
			const variant_id = label?.split("-")[0];
			const variant = data?.variants?.find(
				(variant) => variant?.variant?.id === variant_id,
			);
			const _label = variant?.labels?.find((label) => label?.id === label_id);
			if (_label) {
				label_keys =
					(label_keys?.length > 0 ? label_keys + "," : label_keys) +
					_label?.key;
			}
		});
		return label_keys;
	}, [exception_variant, selected_labels, data?.variants]);

	const find_mix_variant = useCallback(() => {
		const mix_variant_keys = Object.values(selected_labels || {});
		const _variant = data?.mix_variant?.find((variant) => {
			if (variant?.keys?.toString() === ["1-1"]?.toString) {
				return variant;
			} else {
				let condition = true;
				mix_variant_keys?.forEach((key) => {
					if (!variant?.keys?.includes(key)) {
						condition = false;
					}
				});
				return condition;
			}
		});
		set_selected_mix_variant(_variant);

		if (
			Object.keys(selected_labels || {})?.length > 0 &&
			set_query_param &&
			selected_label_keys?.length > 0
		) {
			create_query_string("selected-labels", selected_label_keys);
		}
	}, [selected_labels, data?.mix_variant, selected_label_keys]);

	useEffect(() => {
		find_mix_variant();
	}, [find_mix_variant]);

	// `?selected-labels=` is written with history.replaceState in an effect on mount. A product page drawn on the server
	// mounts while hydrating, before Next patches history.replaceState (an effect of its root, run after the page's
	// effects): the router never learned the address and put its own back on its next update. Writing the same address
	// again once Next is ready keeps it, as when the page mounted after hydration (memory-market).
	useEffect(() => {
		if (!set_query_param) return;
		const id = setTimeout(() => {
			if (new URLSearchParams(window.location.search).has("selected-labels")) {
				window.history.replaceState({}, "", window.location.href);
			}
		}, 0);
		return () => clearTimeout(id);
	}, [set_query_param]);

	const add_to_cart_handler = useCallback(
		async (
			e,
			{
				count,
				success_callback,
				error_callback,
				mix_variant = undefined,
				bundle_id = undefined,
			} = {},
		) => {
			if (e) {
				e?.preventDefault();
			}
			let item;
			if (bundle_id) {
				item = bundle_id;
			} else if (mix_variant) {
				item = mix_variant;
			} else {
				item = selected_mix_variant;
			}
			if (item && count) {
				if (item?.details?.limit_max_reached) {
					toast_configs(t_or("validations.limit_max_reached", "پایان آستانه خرید"), "warning", {
						className: "toast-general-style",
					});
				} else {
					const sub_price_unique_key = item?.price_key
						? `${item?.price_key}-${item?.keys}`
						: undefined;
					set_add_to_cart_loading((prev) => ({
						...prev,
						[sub_price_unique_key || item?.keys || item]: true,
					}));
					const _data = {
						product_id: bundle_id ? undefined : data?.id,
						mix_variant_keys: item?.keys || [],
						count,
						bundle_id,
						price_key: item?.price_key || null,
					};
					if (is_auth) {
						const result = await add_to_cart_mutation({
							product_id: bundle_id ? undefined : data?.id,
							mix_variant: item?.keys || [],
							count,
							lang,
							bundle_id,
							price_key: item?.price_key || null,
						});
						if (result.status) {
							set_cart(result?.data);
							if (is_function(success_callback)) {
								success_callback(result?.data);
							}
						} else {
							if (is_function(error_callback)) {
								error_callback(result?.errors);
							}
						}
					} else {
						const local_cart = parse_local_cart(await get_cookie("local-cart"));
						let _local_cart = [...(local_cart || [])];
						const item_vir_id = `${bundle_id || data?.id}-${item?.keys?.toString()}-${item?.price_key || null}`;
						const changed_item_index = _local_cart?.findIndex((_item) => {
							const local_cart_item_vir_id = `${_item?.bundle_id || _item?.product_id}-${_item?.mix_variant_keys?.toString()}-${_item?.price_key || null}`;
							return local_cart_item_vir_id === item_vir_id;
						});
						if (changed_item_index >= 0) {
							_local_cart[changed_item_index] = _data;
						} else {
							_local_cart = [...(_local_cart || []), _data];
						}
						set_cookie("local-cart", JSON.stringify(_local_cart));
						const result = await get_cart_query({
							lang,
							local_cart: { products: _local_cart },
						});
						if (result?.status) {
							set_cart(result?.data);
							if (is_function(success_callback)) {
								success_callback(result?.data);
							}
						} else {
							if (is_function(error_callback)) {
								error_callback(result?.errors);
							}
						}
					}
					set_add_to_cart_loading((prev) => ({
						...prev,
						[sub_price_unique_key || item?.keys || item]: false,
					}));
				}
			}
		},
		[is_auth, selected_mix_variant, cart?.products, data?.id, lang, t_or],
	);

	const inc_items_in_cart_handler = useCallback(
		async (e, args) => {
			await add_to_cart_handler(e, {
				...args,
				count:
					item_count_in_cart({
						mix_variant: args?.mix_variant ? args?.mix_variant : undefined,
					}) + 1,
			});
		},
		[item_count_in_cart],
	);

	const dec_items_in_cart_handler = useCallback(
		async (e, args) => {
			await add_to_cart_handler(e, {
				...args,
				count:
					item_count_in_cart({
						mix_variant: args?.mix_variant ? args?.mix_variant : undefined,
					}) - 1,
			});
		},
		[item_count_in_cart],
	);

	const change_count_items_in_cart_handler = useCallback(
		async (args) => {
			await add_to_cart_handler(undefined, {
				...args,
			});
		},
		[item_count_in_cart],
	);

	const remove_from_cart_handler = useCallback(
		async (e, args) => {
			e.preventDefault();
			const { success_callback, error_callback, mix_variant, bundle_id } = args;
			let item;
			if (bundle_id) {
				item = bundle_id;
			} else if (mix_variant) {
				item = mix_variant;
			} else {
				item = selected_mix_variant;
			}
			const sub_price_unique_key = item?.price_key
				? `${item?.price_key}-${item?.keys}`
				: undefined;
			set_remove_from_cart_loading((prev) => ({
				...prev,
				[sub_price_unique_key || item?.keys || item]: true,
			}));
			if (is_auth) {
				const result = await remove_from_cart_mutation({
					product_id: bundle_id ? undefined : data?.id,
					mix_variant: item?.keys || [],
					lang,
					bundle_id,
					price_key: item?.price_key || null,
				});
				if (result?.status) {
					set_cart(result?.data);
					if (is_function(success_callback)) {
						success_callback(result?.data);
					}
				} else {
					if (is_function(error_callback)) {
						error_callback(result?.errors);
					}
				}
			} else {
				const local_cart = parse_local_cart(await get_cookie("local-cart"));
				let _local_cart = [...(local_cart || [])];

				const item_vir_id = `${bundle_id || data?.id}-${(item?.keys || [])?.toString()}-${item?.price_key || null}`;
				_local_cart = _local_cart?.filter((_item) => {
					const local_cart_item_vir_id = `${_item?.bundle_id || _item?.product_id}-${(_item?.mix_variant_keys || [])?.toString()}-${_item?.price_key || null}`;
					return local_cart_item_vir_id !== item_vir_id;
				});
				await set_cookie("local-cart", JSON.stringify(_local_cart));
				const result = await get_cart_query({
					lang,
					local_cart: { products: _local_cart },
				});
				if (result?.status) {
					set_cart(result?.data);
					if (is_function(success_callback)) {
						success_callback(result?.data);
					}
				} else {
					if (is_function(error_callback)) {
						error_callback(result?.errors);
					}
				}
			}
			set_remove_from_cart_loading((prev) => ({
				...prev,
				[sub_price_unique_key || item?.keys || item]: false,
			}));
		},
		[data?.id, selected_mix_variant, lang],
	);

	const remove_all_from_cart_handler = useCallback(
		async (e, args) => {
			e.preventDefault();
			const { success_callback, error_callback, products } = args;

			set_remove_from_cart_loading((prev) => ({
				...prev,
				remove_all: true,
			}));
			if (is_auth) {
				const result = await remove_all_from_cart_mutation({
					lang,
					products
				});
				if (result?.status) {
					set_cart(result?.data);
					if (is_function(success_callback)) {
						success_callback(result?.data);
					}
				} else {
					if (is_function(error_callback)) {
						error_callback(result?.errors);
					}
				}
			} else {
				const local_cart = parse_local_cart(await get_cookie("local-cart"));
				let _local_cart = [];

				if (products?.length > 0) {

					_local_cart = local_cart?.filter((_item) => {
						const local_cart_item_vir_id = `${_item?.bundle_id || _item?.product_id}-${(_item?.mix_variant_keys || [])?.toString()}-${_item?.price_key || null}`;
						const product_item = products?.find(p_item => {
							const product_item_vir_id = `${p_item?.bundle_id || p_item?.product_id}-${(p_item?.mix_variant_keys || [])?.toString()}-${p_item?.price_key || null}`;
							return product_item_vir_id === local_cart_item_vir_id
						})

						return !product_item;
					});
				}
				await set_cookie("local-cart", JSON.stringify(_local_cart));
				const result = await get_cart_query({
					lang,
					local_cart: { products: _local_cart },
				});
				if (result?.status) {
					set_cart(result?.data);
					if (is_function(success_callback)) {
						success_callback(result?.data);
					}
				} else {
					if (is_function(error_callback)) {
						error_callback(result?.errors);
					}
				}
			}
			set_remove_from_cart_loading((prev) => ({
				...prev,
				remove_all: false,
			}));
		},
		[data?.id, selected_mix_variant, lang],
	);

	const add_product_to_notifications_handler = useCallback(
		async (e, args) => {
			e.preventDefault();
			const { success_callback, error_callback, mix_variant } = args;
			if (is_auth) {
				let item = selected_mix_variant;
				if (mix_variant) {
					item = mix_variant;
				}
				set_add_to_cart_loading((prev) => ({
					...prev,
					[item?.keys]: true,
				}));
				const result = await add_product_to_notification_mutation({
					product_id: data?.id,
					mix_variant: item?.keys,
					is_notify:
						data?.mix_variant?.length > 0
							? !is_notify[item?.keys]
							: !is_notify[data?.id],
				});
				if (result.status) {
					set_is_notify((prev) => ({
						...prev,
						[item?.keys]:
							data?.mix_variant?.length > 0
								? !prev[item?.keys]
								: !prev[data?.id],
					}));
					if (is_function(success_callback)) {
						success_callback(result?.data);
					}
				} else {
					if (is_function(error_callback)) {
						error_callback(result?.errors);
					}
				}
				set_add_to_cart_loading((prev) => ({
					...prev,
					[selected_mix_variant?.keys]: false,
				}));
			} else {
				toast_configs(t_or("validations.login_first", "لطفا ابتدا وارد بشوید"), "info");
			}
		},
		[is_auth, data?.id, selected_mix_variant, is_notify, t_or],
	);

	const add_product_to_favorites_handler = useCallback(
		async (e, args = {}) => {
			e.preventDefault();
			const {
				success_callback,
				error_callback,
				mix_variant = undefined,
			} = args;
			let item;
			if (mix_variant) {
				item = mix_variant;
			} else {
				item = selected_mix_variant;
			}
			if (is_auth) {
				set_add_to_favorite_loading((prev) => ({
					...prev,
					[data?.id]: true,
				}));
				const result = await add_product_to_favorite_mutation({
					product_id: data?.id,
					mix_variant: item?.keys,
					is_favorite: !is_favorite[data?.id],
				});
				if (result.status) {
					set_is_favorite((prev) => ({
						...prev,
						[data?.id]: !prev[data?.id],
					}));

					if (is_function(success_callback)) {
						success_callback({
							...result?.data,
							is_favorite: !is_favorite[data?.id],
						});
					}
				} else {
					if (is_function(error_callback)) {
						error_callback(result?.errors);
					}
				}
				set_add_to_favorite_loading((prev) => ({
					...prev,
					[data?.id]: false,
				}));
			} else {
				toast_configs(t_or("validations.login_first", "لطفا ابتدا وارد بشوید"), "info");
			}
		},
		[is_auth, data?.id, selected_mix_variant, is_favorite, favorites, t_or],
	);

	const get_favorite_products_handler = async ({
		favorite_product_gql,
		limit = favorites_paginatopn?.limit,
		page = favorites_paginatopn?.page
	}) => {
		set_get_favorites_loading(true);
		const result = await get_favorite_products_query({ limit, page }, { custom_gql: favorite_product_gql });
		if (result.status) {
			set_favorites(result?.data?.data);
			set_favorites_paginatopn(result?.data?.paginate);
		}
		set_get_favorites_loading(false);
	};

	const check_if_all_items_exist = useMemo(() => {
		let exists = [];
		cart?.products?.forEach((item) => {
			const sub_price = item?.product?.mix_variant?.[0]?.sub_prices?.find(
				(price) => price?.price_key === item?.price_key,
			);
			const available =
				(sub_price?.price?.main_price > 0 && sub_price?.details?.count > 0) ||
				(item?.product?.mix_variant?.[0]?.price?.main_price > 0 &&
					item?.product?.mix_variant?.[0]?.details?.count > 0) ||
				(item?.bundle?.price?.main_price > 0 && item?.bundle?.count > 0);
			if (available) {
				exists.push(item?.product_id || item?.bundle_id);
			}
		});
		return exists?.length > 0;
	}, [cart?.products]);

	const out_stock_products_in_cart = useMemo(() => {
		let out_stocks = [];
		cart?.products?.forEach((item) => {
			let unavailable;
			if (item?.bundle) {
				unavailable =
					item?.bundle?.price?.main_price <= 0 || item?.bundle?.count <= 0;
			} else {
				const sub_price = item?.product?.mix_variant?.[0]?.sub_prices?.find(
					(price) => price?.price_key === item?.price_key,
				);

				if (sub_price) {
					unavailable =
						sub_price?.price?.main_price <= 0 || sub_price?.details?.count <= 0;
				} else {
					unavailable =
						item?.product?.mix_variant?.[0]?.price?.main_price <= 0 ||
						item?.product?.mix_variant?.[0]?.details?.count <= 0;
				}
			}

			if (unavailable) {
				out_stocks.push(item);
			}
		});
		return out_stocks;
	}, [cart?.products]);

	const offer_timer = useCallback((product) => get_offer_remaining_seconds(product), []);

	return {
		selected_mix_variant,
		selected_labels,
		select_variant_handler,
		add_to_cart_handler,
		item_count_in_cart,
		check_product_availability,
		check_product_offer,
		mix_variant_limit_min,
		mix_variant_limit_max,
		inc_items_in_cart_handler,
		dec_items_in_cart_handler,
		remove_from_cart_handler,
		is_notify,
		add_product_to_notifications_handler,
		is_favorite,
		add_product_to_favorites_handler,
		get_favorite_products_handler,
		favorites,
		set_favorites,
		add_to_cart_loading,
		remove_from_cart_loading,
		add_to_favorite_loading,
		get_favorites_loading,
		favorites_paginatopn,
		set_favorites_paginatopn,
		change_count_items_in_cart_handler,
		check_if_all_items_exist,
		out_stock_products_in_cart,
		exception_variant,
		selected_label_keys,
		offer_timer,
		available_variants,
		variants_with_disabled_unavailable,
		remove_all_from_cart_handler
	};
};

const useProduct = (
	data = {},
	exception_variant_key = null,
	set_query_param = false,
	default_favorites_limit = 15
) => {
	const search_params = useSearchParams();
	const selected_labels_query = search_params.get("selected-labels");
	// `?sku=` / a Torob link preselect their combination, as on core 14 (product/selection.js)
	const preselected = useMemo(
		() => get_preselected_selection(data, search_params),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[data, search_params?.get("sku"), search_params?.get("page-source")],
	);
	return useProductBase(data, exception_variant_key, set_query_param, default_favorites_limit, {
		initial_labels: preselected ? preselected.initial_labels : selected_labels_query,
		preselected_label_ids: preselected?.preselected_label_ids,
	});
};

export default useProduct;
